<template>
  <div class="predictive-search">
    <div class="form__large">
      <form :action="submitUrl" ref="searchForm">
        <div
          id="predictive-search__status"
          role="alert"
          aria-atomic="true"
          aria-live="polite"
          class="sr-only"
          v-html="status"
        ></div>
        <input
          role="combobox"
          class="form__input--large"
          type="text"
          name="s"
          placeholder="Search"
          autocomplete="list"
          aria-haspopup="listbox"
          aria-controls="search-suggestions"
          aria-owns="search-suggestions"
          :aria-expanded="suggestions.length > 0"
          aria-activedescendant=""
          aria-describedby="predictive-search__assistiveHint"
          ref="searchTextInput"
          v-on:keyup="handleSearchInputKeyup($event)"
          v-on:keydown="handleSearchInputKeydown($event)"
          v-on:focus="handleSearchInputFocus"
          v-on:blur="handleSearchInputBlur($event)"
          v-on:input="handleSearchInputChange"
        />

        <div
          v-show="showSuggestions"
          class="predictive-search__suggestions-container"
        >
          <div
            v-show="isLoading"
            class="predictive-search__suggestions-loading spinner"
            role="status"
            aria-label="Loading"
          >
            <span class="sr-only">Loading...</span>
          </div>
          <ul
            v-show="suggestions.length > 0"
            class="predictive-search__suggestion-list"
            id="search-suggestions"
            role="listbox"
          >
            <li
              role="option"
              v-for="(suggestion, index) in suggestions"
              v-bind:key="index"
            >
              <a
                class="predictive-search__suggestion"
                :href="suggestion.url"
                tabindex="0"
                v-html="suggestion.text"
              />
            </li>
          </ul>
        </div>

        <span id="predictive-search__assistiveHint" style="display: none"
          >Up to five top results will be available. Use up and down arrows to
          review and enter to select. Touch device users, explore by touch or
          with swipe gestures. Submit the form for a list of all results.</span
        >

        <button>
          <ahc-icon icon-name="search" width="48" height="35"></ahc-icon>
          <span class="sr-only">Submit search</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script lang="js">
import ahcIcon from "../icon/icon.vue";
import axios from "axios";

export default {
  name: "ahc-predictive-search",
  components: {
    ahcIcon,
  },
  props: {
    endpoint: {
      type: String,
      required: true,
    },
    submitUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      suggestions: [],
      showSuggestions: false,
      lastSearchText: "",
      searchTimeout: null,
      isLoading: false,
      totalResults: null,
      status: "",
    };
  },
  mounted() {},
  watch: {
    // when status is updated, clear it after 3 seconds
    status() {
      setTimeout(() => {
        this.status = "";
      }, 3000);
    },
  },
  methods: {
    async getSuggestions(searchText) {
      const vm = this;

      clearTimeout(vm.searchTimeout);

      vm.searchTimeout = setTimeout(async () => {
        if (searchText.length > 2) {
          searchText = searchText.toLowerCase();

          try {
            vm.isLoading = true;

            let response = await axios.get(vm.endpoint, {
              params: {
                s: searchText,
              },
            });

            if (response.data.suggestions) {
              vm.suggestions = [];
              vm.totalResults = response.data.suggestions.length;

              for (let i = 0; i < vm.totalResults && i < 5; i++) {
                // Before we add the suggestion, we need to highlight the entered text.
                let suggestion = response.data.suggestions[i];
                let suggestionTextLower = suggestion.text.toLowerCase();

                let suggestionText = suggestion.text;
                if (suggestionTextLower.includes(searchText)) {
                  // Let's providing some markup to highlight the string in the suggestion.
                  const startIndex = suggestionTextLower.indexOf(searchText);
                  let textToReplace = suggestion.text.substring(
                    startIndex,
                    startIndex + searchText.length
                  );
                  let highlightedText = `<strong>${textToReplace}</strong>`;

                  suggestionText = suggestionText.replace(
                    textToReplace,
                    highlightedText
                  );
                }

                vm.suggestions.push({
                  text: suggestionText,
                  url: suggestion.url,
                });
              }

              // Set the total number of results, since we cap "top" results at 5
              if (response.data.suggestions.length !== 0) {
                vm.status = `Listing ${vm.suggestions.length} top results. Submit for full results list.`;
              }

              vm.showSuggestions = true;
            }
            if (vm.suggestions.length === 0) {
              vm.status = "No results available.";
            }
          } catch (error) {
            // Log any errors that have occurred.
            console.log(error);
          } finally {
            vm.isLoading = false;
          }
        } else {
          vm.resetSuggestions();
        }
      }, 250);
    },
    /**
     * Keyup event handler for the insurer name text input.
     *
     * @param event
     */
    handleSearchInputKeyup(event) {
      // Will have to handle different key presses.
      const vm = this;
      const key = event.keyCode;
      const searchTextInput = vm.$refs.searchTextInput;

      switch (key) {
        // Enter.
        case 13:
          event.preventDefault();

          break;

        // Escape.
        case 27:
          searchTextInput.blur();

          break;

        default:
          // Execute the suggestion search.
          if (vm.lastSearchText !== event.target.value) {
            vm.lastSearchText = event.target.value;
            vm.getSuggestions(event.target.value);
          } else {
            vm.showSuggestions = true;
          }

          break;
      }
    },
    /**
     * Keydown is needed to handle navigating to the suggestions list, and selecting
     * a suggestion via enter, but maintaining focus in the search text input.
     *
     * @param event
     */
    handleSearchInputKeydown(event) {
      const vm = this;
      const key = event.keyCode;

      switch (key) {
        case 13: {
          event.preventDefault();
          let currentHighlightedSuggestion = document.querySelector(
            ".predictive-search__suggestion.highlighted"
          );

          if (currentHighlightedSuggestion !== null) {
            currentHighlightedSuggestion.click();
          } else {
            vm.$refs.searchForm.submit();
          }

          break;
        }
        // Up arrow.
        case 38:
          event.preventDefault();
          vm.selectSuggestion("prev");

          break;

        // Down arrow.
        case 40:
          event.preventDefault();
          vm.selectSuggestion("next");

          break;
      }
    },
    /**
     * Handles focus gain for the search text field.
     */
    handleSearchInputFocus() {
      const vm = this;

      vm.showSuggestions = true;

      if (vm.$refs.searchTextInput.value !== vm.lastSearchText) {
        vm.getSuggestions(vm.$refs.searchTextInput.value);
      }
    },
    handleSearchInputBlur(event) {
      const vm = this;

      let nextFocusedElement = event.relatedTarget;

      if (
        nextFocusedElement === null ||
        !nextFocusedElement.classList.contains("predictive-search__suggestion")
      ) {
        vm.showSuggestions = false;
      }

      this.$refs.searchTextInput.removeAttribute("aria-activedescendant");
    },
    handleSearchInputChange() {},
    resetSuggestions() {
      this.suggestions = [];
    },
    /**
     * Handles navigating through the list of suggestions.
     *
     * @param direction
     *   Either `next` or `prev`.
     */
    selectSuggestion(direction) {
      const vm = this;

      // Get the currently highlighted suggestion, and the first and last suggestions in the list.
      let currentHighlightedSuggestion = document.querySelector(
        ".predictive-search__suggestion.highlighted"
      );
      let suggestionElements = Array.from(
        document.querySelectorAll(".predictive-search__suggestion")
      );
      let firstSuggestion = suggestionElements[0];
      let lastSuggestion = suggestionElements[suggestionElements.length - 1];

      if (currentHighlightedSuggestion !== null) {
        // There is a currently highlighted suggestion.
        // First, remove all existing highlights.
        vm.removeSuggestionHighlights();

        if (direction === "next") {
          // Retrieve the next element.

          if (currentHighlightedSuggestion === lastSuggestion) {
            // If the current highlighted and last suggestion are the same, and we're moving to the next,
            // make sure there are no highlighted suggestions.
            vm.removeSuggestionHighlights();
          } else {
            // Highlight the next suggestion element.
            vm.setSuggestionElementStatus(
              currentHighlightedSuggestion.parentElement.nextElementSibling
                .firstElementChild,
              "active"
            );
          }
        } else {
          // The only other direction is previous.
          if (currentHighlightedSuggestion === firstSuggestion) {
            // If we're currently on the first suggestion, and the user is tapping
            // the up arrow, then we ensure no suggestions are highlighted.
            vm.removeSuggestionHighlights();
          } else {
            vm.setSuggestionElementStatus(
              currentHighlightedSuggestion.parentElement.previousElementSibling
                .firstElementChild,
              "active"
            );
          }
        }
      } else {
        // There is currently no suggestion highlighted. This means the user has yet to navigate into the list
        // of suggestions. In that case, we only allow for arrow down actions to enter the list.
        if (direction === "next") {
          vm.setSuggestionElementStatus(firstSuggestion, "active");
        } else {
          vm.setSuggestionElementStatus(lastSuggestion, "active");
        }
      }
    },
    removeSuggestionHighlights() {
      document
        .querySelectorAll(".predictive-search__suggestion")
        .forEach((element) => {
          this.setSuggestionElementStatus(element, "inactive");
        });
    },
    setSuggestionElementStatus(element, status) {
      let searchTextInput = this.$refs.searchTextInput;

      if (status === "active") {
        element.classList.add("highlighted");
        element.setAttribute("aria-selected", "true");
        searchTextInput.setAttribute("aria-activedescendant", element.id);
      } else {
        element.classList.remove("highlighted");
        element.setAttribute("aria-selected", "false");
        searchTextInput.removeAttribute("aria-activedescendant");
      }
    },
  },
};
</script>
